/*
 * JE-CUSTOM: entire file (not part of stock Warehouse).
 *
 * JEG appointments embed, for sections/jeg-appointments.liquid (contract
 * docs/appointments-site-contract.md §10.6 and §11.3).
 *
 * Every visitor gets the tool, loaded from the app origin (the section's
 * "App origin" setting) in a borderless iframe. Guests book, request and see
 * their appointments without an account; signing in is optional and fills in
 * their details. Liquid (`data-signed-in`) only decides where the frame
 * starts:
 *
 * - Signed in to the store: `/appointments/embed/start?view=…`, which signs
 *   the customer in to the tool with their store account (a few redirects,
 *   no clicks) and then shows the page.
 * - Signed out: `/appointments/embed/guest?view=…`, which ends any
 *   appointment sign-in left in this browser from an earlier store sign-in
 *   (so the tool never says "Signed in" while the store says signed out) and
 *   then shows the page as a guest, with no sign-in hand-off. The pages offer
 *   an optional "Sign in to fill in your details" link of their own, which
 *   opens the store's sign-in in the whole window.
 *
 * Either way the frame stays hidden until the page inside says
 * `ready {visible:true}`, so before launch visitors without the tester link
 * see nothing new. It then follows the page's height and, when the page
 * asks, scrolls the frame's top (`scroll-top`, another page) or a spot inside
 * it (`scroll-to`, a form result) into view.
 *
 * The page URL's `appointments` (book | my) picks the view; `#book` or an
 * `appointments` loads it right away and scrolls to it. Never `view` on this
 * page: Shopify reserves `?view=` to pick another template, so
 * `/pages/appointment?view=my` renders without this section. `view` is still
 * read as a fallback, in case a template ever renders the section with it.
 * Messages are accepted only from the exact app origin and from this
 * section's own frame.
 *
 * Private test links never come through here: they open the app origin,
 * which stores the token and redirects to this page without it (§10.6). The
 * theme's analytics see a storefront address before any script can change
 * it, so a token on this page's URL is never forwarded to the frame. As a
 * precaution this script still removes a stray `access` from the address bar
 * as soon as it runs.
 *
 * Mirrors deck-builder/lib/appointments/embed/protocol.ts and
 * lib/appointments/ui/routes.ts; a deck-builder test
 * (theme-appointments-embed.test.ts) keeps them in step.
 */
(function (win) {
  "use strict";

  var MESSAGE = {
    ready: "jeg-appointments:ready",
    height: "jeg-appointments:height",
    scrollTop: "jeg-appointments:scroll-top",
    scrollTo: "jeg-appointments:scroll-to",
  };
  var START_PATH = "/appointments/embed/start";
  var GUEST_PATH = "/appointments/embed/guest";
  var SECTION_ANCHOR = "book";
  // The page URL's view parameter (lib/appointments/ui/routes.ts STOREFRONT_VIEW_PARAM).
  var PAGE_VIEW_PARAM = "appointments";
  var MAX_HEIGHT = 20000;
  // Space kept above a scrolled-to spot (matches the section's scroll-margin-top).
  var SCROLL_GAP = 16;
  // A spot this close to the bottom edge doesn't count as in view.
  var VISIBLE_MARGIN = 48;
  var ROOT_SELECTOR = "[data-jeg-appointments]";
  var MOUNTED = "data-jeg-appointments-mounted";

  function isLocalHostname(hostname) {
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]"
      || /\.localhost$/.test(hostname);
  }

  /** The app origin from the section setting, or null when it is not a usable origin. */
  function appOriginFrom(value) {
    var url;
    try {
      url = new URL(String(value || "").trim());
    } catch (error) {
      return null;
    }
    if (url.protocol === "https:" || (url.protocol === "http:" && isLocalHostname(url.hostname))) {
      return url.origin;
    }
    return null;
  }

  /** `book` or `my` from the page URL (`?appointments=`, else `?view=`), or null. */
  function pageView(url) {
    var names = [PAGE_VIEW_PARAM, "view"];
    for (var i = 0; i < names.length; i += 1) {
      var value = url.searchParams.get(names[i]);
      if (value === "book" || value === "my") return value;
    }
    return null;
  }

  /** `my` for `?appointments=my` (or `?view=my`), otherwise `book`. */
  function viewFrom(pageHref) {
    return pageView(new URL(pageHref)) === "my" ? "my" : "book";
  }

  /**
   * The visitor came for the tool: a view (an email link, or the store's
   * sign-in coming back with `?appointments=book`), or the section's `#book`
   * anchor.
   */
  function cameForTool(pageHref) {
    var url = new URL(pageHref);
    return pageView(url) !== null || url.hash === "#" + SECTION_ANCHOR;
  }

  /**
   * Where the frame starts, with `?view=book|my` and nothing else from the
   * page URL (never `access`):
   * - signed in: `<app origin>/appointments/embed/start`, which signs the
   *   store customer in to the tool and opens that view;
   * - signed out (anything but `signedIn === true`):
   *   `<app origin>/appointments/embed/guest`, which ends any appointment
   *   sign-in left in this browser from an earlier store sign-in, then opens
   *   that view as a guest.
   */
  function frameUrl(appOrigin, pageHref, signedIn) {
    var origin = appOriginFrom(appOrigin);
    if (!origin) return null;
    var view = viewFrom(pageHref);
    var url = new URL(signedIn === true ? START_PATH : GUEST_PATH, origin);
    url.searchParams.set("view", view);
    return url.toString();
  }

  /** This page's address without `access`, or null when there is nothing to remove. */
  function addressWithoutAccess(pageHref) {
    var url = new URL(pageHref);
    if (!url.searchParams.has("access")) return null;
    url.searchParams.delete("access");
    return url.pathname + url.search + url.hash;
  }

  /** A message from the frame, or null for anything else (wrong origin, wrong window, junk). */
  function readMessage(event, appOrigin, frameWindow) {
    if (!event || !appOrigin || event.origin !== appOrigin) return null;
    if (!frameWindow || event.source !== frameWindow) return null;
    var data = event.data;
    if (!data || typeof data !== "object") return null;
    if (data.type === MESSAGE.ready) return { type: "ready", visible: data.visible === true };
    if (data.type === MESSAGE.height) {
      var height = Number(data.height);
      if (!isFinite(height) || height < 0) return null;
      return { type: "height", height: Math.min(Math.ceil(height), MAX_HEIGHT) };
    }
    if (data.type === MESSAGE.scrollTop) return { type: "scroll-top" };
    if (data.type === MESSAGE.scrollTo) {
      var top = Number(data.top);
      if (!isFinite(top) || top < 0) return null;
      return { type: "scroll-to", top: Math.min(Math.round(top), MAX_HEIGHT) };
    }
    return null;
  }

  /** Height of the theme's sticky header, so "in view" means below it. */
  function stickyOffset(w) {
    try {
      var style = w.getComputedStyle(w.document.documentElement);
      var sticky = parseFloat(style.getPropertyValue("--header-is-sticky")) || 0;
      var header = parseFloat(style.getPropertyValue("--header-height")) || 0;
      return sticky * header;
    } catch (error) {
      return 0;
    }
  }

  function prefersReducedMotion(w) {
    try {
      return Boolean(w.matchMedia && w.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch (error) {
      return false;
    }
  }

  function scrollBehavior(w) {
    return prefersReducedMotion(w) ? "auto" : "smooth";
  }

  /**
   * The tool itself, for everyone: signed in it starts with the account
   * sign-in hand-off, signed out it opens the view directly.
   */
  function mountFrame(root, w, pageHref, appOrigin, signedIn) {
    var doc = root.ownerDocument;
    var src = frameUrl(appOrigin, pageHref, signedIn);
    if (!src) return null;

    // A visitor who came for the tool (an email link, `#book`, the test
    // link's redirect, the store sign-in coming back) gets it loaded and
    // scrolled into view right away; everyone else loads it lazily.
    var focused = cameForTool(pageHref);

    var frame = doc.createElement("iframe");
    frame.className = "jeg-appointments__frame";
    frame.setAttribute("title", root.getAttribute("data-frame-title") || "Book or manage a selling appointment");
    frame.setAttribute("loading", focused ? "eager" : "lazy");
    // The page inside only learns this page's origin, never its address.
    frame.setAttribute("referrerpolicy", "origin");
    frame.setAttribute("src", src);
    root.appendChild(frame);

    var state = { visible: false, height: 0, readyCount: 0, scrolled: false };

    function applyHeight() {
      frame.style.height = state.visible && state.height > 0 ? state.height + "px" : "";
    }

    function setVisible(visible) {
      state.visible = visible;
      root.classList.toggle("is-visible", visible);
      root.classList.toggle("is-empty", !visible);
      applyHeight();
    }

    function viewportHeight() {
      return w.innerHeight || doc.documentElement.clientHeight || 0;
    }

    function scrollToTop(always) {
      if (!state.visible) return;
      var top = root.getBoundingClientRect().top;
      if (!always && top >= stickyOffset(w) && top < viewportHeight()) return;
      root.scrollIntoView({ block: "start", behavior: scrollBehavior(w) });
    }

    /**
     * Brings the spot `offset` pixels below the frame's top into view, just
     * under the sticky header, unless it is already on screen. The offset is
     * clamped to the frame's known height.
     */
    function scrollToOffset(offset) {
      if (!state.visible) return;
      var clamped = Math.max(0, Math.min(offset, state.height > 0 ? state.height : MAX_HEIGHT));
      var spot = frame.getBoundingClientRect().top + clamped;
      var header = stickyOffset(w);
      if (spot >= header && spot <= viewportHeight() - VISIBLE_MARGIN) return;
      var current = w.pageYOffset || w.scrollY || 0;
      var target = Math.max(0, Math.round(current + spot - header - SCROLL_GAP));
      try {
        w.scrollTo({ top: target, behavior: scrollBehavior(w) });
      } catch (error) {
        w.scrollTo(0, target);
      }
    }

    function onMessage(event) {
      var message = readMessage(event, appOrigin, frame.contentWindow);
      if (!message) return;
      if (message.type === "ready") {
        state.readyCount += 1;
        setVisible(message.visible);
        if (message.visible && focused && !state.scrolled) {
          state.scrolled = true;
          scrollToTop(true);
        } else if (message.visible && state.readyCount > 1) {
          // Another page loaded inside the frame.
          scrollToTop(false);
        }
      } else if (message.type === "height") {
        state.height = message.height;
        applyHeight();
      } else if (message.type === "scroll-to") {
        scrollToOffset(message.top);
      } else {
        scrollToTop(false);
      }
    }

    w.addEventListener("message", onMessage);
    return {
      frame: frame,
      destroy: function () {
        w.removeEventListener("message", onMessage);
      },
    };
  }

  function mount(root, w, pageHref) {
    var appOrigin = appOriginFrom(root.getAttribute("data-app-origin"));
    if (!appOrigin) return null;
    return mountFrame(root, w, pageHref, appOrigin, root.getAttribute("data-signed-in") === "true");
  }

  /** Removes a stray `access` token from the address bar, history and shares. */
  function dropAccessFromAddress(w, pageHref) {
    var address = addressWithoutAccess(pageHref);
    if (address === null || !w.history || typeof w.history.replaceState !== "function") return;
    try {
      w.history.replaceState(w.history.state, "", address);
    } catch (error) {
      // Leave the address as it is.
    }
  }

  function start(w) {
    var doc = w.document;
    var pageHref = String(w.location.href);
    // First, before anything waits for the page: it is never forwarded.
    dropAccessFromAddress(w, pageHref);

    function mountAll(scope) {
      var roots = (scope || doc).querySelectorAll(ROOT_SELECTOR);
      for (var i = 0; i < roots.length; i += 1) {
        if (roots[i].getAttribute(MOUNTED) === "true") continue;
        roots[i].setAttribute(MOUNTED, "true");
        mount(roots[i], w, pageHref);
      }
    }

    if (doc.readyState === "loading") {
      doc.addEventListener("DOMContentLoaded", function () {
        mountAll(null);
      });
    } else {
      mountAll(null);
    }
    // The theme editor re-renders sections in place.
    doc.addEventListener("shopify:section:load", function (event) {
      mountAll(event.target);
    });
  }

  win.JEGAppointmentsEmbed = Object.freeze({
    MESSAGE: MESSAGE,
    appOriginFrom: appOriginFrom,
    viewFrom: viewFrom,
    frameUrl: frameUrl,
    addressWithoutAccess: addressWithoutAccess,
    readMessage: readMessage,
    mount: mount,
  });
  start(win);
})(window);
